import { getSavedAuthData } from '../../../../api/axios/interceptor'

/**
 * Fetch a case's source image — the plate as the PIPELINE saw it (PDF
 * rasterised, pages stacked, TIFF converted), which is the only geometry the
 * detection boxes fall on. The original upload is not that image.
 *
 * Two candidates, in order: the authenticated endpoint, then the static one
 * for cases filed before it existed. `public/uploads` is a symlink into the
 * server's shared directory and is not served by nginx on staging, so the
 * authenticated route is the one that answers for anything recent.
 */
export async function fetchSourceImageBlob(
  apiDomain: string,
  fileUrl: string | null | undefined,
  /** Le fichier TEL QU'IL A ÉTÉ DÉPOSÉ, sans l'aperçu que la route tire des
   *  PDF et des TIFF pour que le navigateur puisse les afficher. */
  original = false,
): Promise<Blob | null> {
  if (!fileUrl) return null
  const rawPath = fileUrl.replace(/^\/?(public\/)?uploads\//, '') + (original ? '?original=1' : '')
  const { accessToken } = getSavedAuthData()
  const headers = { Authorization: accessToken || '' }
  for (const url of [`${apiDomain}/api/uploads/${rawPath}`, `${apiDomain}/uploads/${rawPath}`]) {
    try {
      const response = await fetch(url, { headers })
      if (response.ok) return await response.blob()
    } catch {
      // Try the next candidate; only an exhausted list is a failure.
    }
  }
  return null
}

/** Extension that matches what the server actually sent. */
const EXTENSION_FOR_TYPE: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/webp': 'webp',
  'image/tiff': 'tif',
  'application/pdf': 'pdf',
}

/**
 * The name the file is saved under: the one the pipeline gave it, which
 * already carries the plate's own name — not the case's original `filename`,
 * which names the upload rather than the image made from it.
 *
 * L'extension suit les OCTETS, pas le nom. Le garde-fou reste utile même
 * maintenant que le fichier d'origine est servi tel quel : un scanner rend
 * parfois un TIFF sous un nom en .png, et le fichier atterrirait illisible.
 */
export function sourceImageFilename(
  fileUrl: string | null | undefined,
  contentType?: string | null,
): string {
  const base = (fileUrl ?? '').split('/').pop() || 'image-brute'
  const wanted = EXTENSION_FOR_TYPE[(contentType ?? '').split(';')[0].trim().toLowerCase()]
  if (!wanted) return base
  const stem = base.replace(/\.[^.]+$/, '')
  return `${stem}.${wanted}`
}

/**
 * Enregistre le fichier d'origine du cas — celui que le biologiste a déposé.
 *
 * Pas l'aperçu : une plaque envoyée en PDF doit revenir en PDF. La route rend
 * sinon une image rastérisée, utile pour l'afficher à l'écran et trompeuse
 * dans un dossier, où l'on croirait tenir le document d'origine.
 *
 * Rend false quand le fichier n'a pas pu être récupéré, pour que l'appelant le
 * dise plutôt que d'échouer en silence.
 */
export async function downloadSourceImage(
  apiDomain: string,
  fileUrl: string | null | undefined,
): Promise<boolean> {
  const blob = await fetchSourceImageBlob(apiDomain, fileUrl, true)
  if (!blob) return false
  const href = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = href
  link.download = sourceImageFilename(fileUrl, blob.type)
  document.body.appendChild(link)
  link.click()
  link.remove()
  // Revoked on the next tick: Safari reads the href after the click returns.
  setTimeout(() => URL.revokeObjectURL(href), 0)
  return true
}
